/**
 * Guided setup — the PURE state model behind Admin → Setup.
 *
 * Three surfaces, one module, zero IO:
 *
 *  1. Company-brain walkthrough: a four-step wizard (voice → positioning →
 *     offers → ICP). Each step maps to ONE deterministic BrandEntry id, so
 *     saving always upserts the same records — the first three ids are
 *     EXACTLY the canonical ids the Company Brain editor owns
 *     (src/lib/brandBrain/editor.ts), so finishing the walkthrough IS a
 *     populated Brand Brain that immediately feeds content / proposals /
 *     blog / email composition (src/lib/brandBrain/compose.ts). The ICP
 *     step adds a fourth deterministic entry the composer picks up the
 *     same way ('all' scopes, positioning kind).
 *
 *  2. Org KPIs / targets: a small, JSON-safe list model with a TOTAL
 *     parser (never throws; malformed payloads degrade to []), persisted
 *     by ./setupPersist.ts as one synthetic org-scoped brainFacts row —
 *     the identical mechanism the Brand Brain + onboarding flag use.
 *
 *  3. Governance notes (SLA + process): a two-field text model with the
 *     same TOTAL parse/serialize discipline.
 *
 * No store import, no clock, no crypto beyond an id fallback — callers
 * supply `now`/`updatedBy` and persist results themselves, which keeps
 * every decision here unit-testable.
 */

import type { BrandBrain, BrandEntry, BrandEntryKind } from './brandBrain/schema';
import { archiveBrandEntry, upsertBrandEntry } from './brandBrain/store';
import { BRAND_FIELD_MAX } from './brandBrain/editor';

// ---------------------------------------------------------------------------
// Step catalog
// ---------------------------------------------------------------------------

/** The four walkthrough steps, in guided order. */
export type SetupStepKey = 'voice' | 'positioning' | 'offers' | 'icp';

export interface SetupStepMeta {
  key: SetupStepKey;
  /** Deterministic BrandEntry id — the same step always upserts the same
   *  record. voice/positioning/offers reuse the Company Brain editor's
   *  canonical ids so both surfaces edit the SAME entries. */
  id: string;
  kind: BrandEntryKind;
  /** Entry title + step label. */
  label: string;
  /** The guided question the operator answers. */
  question: string;
  /** A concrete example shown as the placeholder. */
  example: string;
  /** What this step powers downstream — the "why it's worth typing". */
  feeds: string;
  /** Prompt-composition priority (weight desc wins the token budget). */
  weight: number;
}

export const SETUP_STEPS: readonly SetupStepMeta[] = [
  {
    key: 'voice',
    id: 'brand-voice',
    kind: 'voice',
    label: 'Voice & tone',
    question: 'How should everything we write sound?',
    example:
      'Plainspoken, confident, zero hype. Short sentences. No exclamation marks, no buzzwords.',
    feeds: 'Sets the register of every generated blog, email, and proposal.',
    weight: 5,
  },
  {
    key: 'positioning',
    id: 'brand-positioning',
    kind: 'positioning',
    label: 'Positioning',
    question: 'What do you do, for whom, and why you over the field?',
    example:
      'We give 5–50 person agencies an operations brain: CRM, content, and proposals run by governed AI instead of three disconnected tools.',
    feeds: 'Anchors every value-prop line the AI writes — no generic filler.',
    weight: 4,
  },
  {
    key: 'offers',
    id: 'brand-offers',
    kind: 'offer',
    label: 'Key offers',
    question: 'What do you actually sell — packages, outcomes, price anchors?',
    example:
      'Pilot: 4-week governed-AI rollout, $6k. Scale: full workspace + 90-day success plan, $2.5k/mo.',
    feeds: 'Keeps proposals and outreach concrete: real offers, real numbers.',
    weight: 3,
  },
  {
    key: 'icp',
    id: 'brand-icp',
    kind: 'positioning',
    label: 'Ideal customer (ICP)',
    question: 'Who is the buyer — company shape, role, and the pains that make them move?',
    example:
      'Founder/COO at a 5–50 person B2B services firm; drowning in tool sprawl; wants leverage without hiring ops.',
    feeds: 'Tunes personas, prospect scoring, and who content speaks to.',
    weight: 4,
  },
] as const;

const STEP_BY_KEY: Record<SetupStepKey, SetupStepMeta> = Object.fromEntries(
  SETUP_STEPS.map((s) => [s.key, s]),
) as Record<SetupStepKey, SetupStepMeta>;

export function setupStepMeta(key: SetupStepKey): SetupStepMeta {
  return STEP_BY_KEY[key];
}

// ---------------------------------------------------------------------------
// Walkthrough state (pure derivation from a BrandBrain)
// ---------------------------------------------------------------------------

export type SetupStepValues = Record<SetupStepKey, string>;

/** Read each step's current text out of the brain. Archived entries read
 *  as '' (the operator cleared them) — the honest reload path. */
export function stepValuesFromBrain(brain: BrandBrain): SetupStepValues {
  const values: SetupStepValues = { voice: '', positioning: '', offers: '', icp: '' };
  for (const s of SETUP_STEPS) {
    const entry = brain.entries.find((e) => e.id === s.id);
    values[s.key] = entry && entry.status !== 'archived' ? entry.body : '';
  }
  return values;
}

export interface SetupStepState {
  key: SetupStepKey;
  label: string;
  done: boolean;
}

export interface WalkthroughState {
  steps: SetupStepState[];
  doneCount: number;
  total: number;
  /** 0–100 integer, for the progress bar. */
  percent: number;
  /** First incomplete step in guided order; null when everything is done. */
  nextKey: SetupStepKey | null;
  complete: boolean;
}

/**
 * Derive the whole walkthrough state from the persisted brain. A step is
 * DONE when its deterministic entry exists, is not archived, and has a
 * non-blank body — typing without saving never counts.
 */
export function walkthroughState(brain: BrandBrain): WalkthroughState {
  const values = stepValuesFromBrain(brain);
  const steps: SetupStepState[] = SETUP_STEPS.map((s) => ({
    key: s.key,
    label: s.label,
    done: values[s.key].trim().length > 0,
  }));
  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const next = steps.find((s) => !s.done);
  return {
    steps,
    doneCount,
    total,
    percent: Math.round((doneCount / total) * 100),
    nextKey: next ? next.key : null,
    complete: doneCount === total,
  };
}

// ---------------------------------------------------------------------------
// Step validation + apply
// ---------------------------------------------------------------------------

/**
 * Validate one step's text before saving. Returns a human-readable error,
 * or null when the text may be saved. Blank is an error here (the
 * walkthrough is "answer, then continue" — clearing a saved step goes
 * through applyWalkthroughStep with '' explicitly).
 */
export function validateStepText(key: SetupStepKey, text: string): string | null {
  const meta = setupStepMeta(key);
  const len = text.trim().length;
  if (len === 0) {
    return `Write a sentence or two for ${meta.label} — an empty step teaches the AI nothing.`;
  }
  if (len > BRAND_FIELD_MAX) {
    return `${meta.label} is ${len - BRAND_FIELD_MAX} characters over the ${BRAND_FIELD_MAX}-character limit.`;
  }
  return null;
}

export interface ApplyStepOptions {
  /** Operator name / email stamped on the entry. */
  updatedBy: string;
  /** ISO-8601 UTC timestamp — injected so tests are deterministic. */
  now: string;
}

/**
 * Apply one step's answer to the brain. Non-blank text upserts the step's
 * canonical entry ('all' scopes — org-level brand knowledge routes to
 * every output surface); blank text archives an existing entry (soft
 * delete, history intact) and is a no-op otherwise. IMMUTABLE: returns a
 * new BrandBrain, never mutates the input.
 */
export function applyWalkthroughStep(
  brain: BrandBrain,
  key: SetupStepKey,
  text: string,
  opts: ApplyStepOptions,
): BrandBrain {
  const meta = setupStepMeta(key);
  const body = text.trim();
  if (!body) {
    return brain.entries.some((e) => e.id === meta.id && e.status !== 'archived')
      ? archiveBrandEntry(brain, meta.id)
      : brain;
  }
  const entry: BrandEntry = {
    id: meta.id,
    kind: meta.kind,
    title: meta.label,
    body,
    weight: meta.weight,
    scopes: ['all'],
    status: 'active',
    updatedBy: opts.updatedBy.trim() || 'operator',
    updatedAt: opts.now,
  };
  return upsertBrandEntry(brain, entry);
}

// ---------------------------------------------------------------------------
// Org KPIs / targets — JSON-safe list model with a TOTAL parser
// ---------------------------------------------------------------------------

export type KpiCadence = 'weekly' | 'monthly' | 'quarterly';

export const KPI_CADENCES: readonly KpiCadence[] = ['weekly', 'monthly', 'quarterly'];

export interface OrgKpi {
  id: string;
  /** What we measure, e.g. "Qualified pipeline". */
  name: string;
  /** How it's measured, e.g. "$ of stage-2+ opportunities". */
  metric: string;
  /** The target, e.g. "$250k / quarter". */
  target: string;
  cadence: KpiCadence;
  /** Who owns hitting it. */
  owner: string;
}

let kpiCounter = 0;
function makeKpiId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  kpiCounter += 1;
  return `kpi-${Date.now().toString(36)}-${kpiCounter}`;
}

/** Fill a partial row into a complete OrgKpi. Never mutates the input. */
export function makeOrgKpi(partial: Partial<OrgKpi> = {}): OrgKpi {
  return {
    id: partial.id ?? makeKpiId(),
    name: partial.name ?? '',
    metric: partial.metric ?? '',
    target: partial.target ?? '',
    cadence: partial.cadence ?? 'monthly',
    owner: partial.owner ?? '',
  };
}

export function serializeOrgKpis(kpis: OrgKpi[]): string {
  return JSON.stringify({ kpis });
}

function coerceKpi(raw: unknown): OrgKpi | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || r.id.length === 0) return null;
  if (typeof r.name !== 'string') return null;
  return {
    id: r.id,
    name: r.name,
    metric: typeof r.metric === 'string' ? r.metric : '',
    target: typeof r.target === 'string' ? r.target : '',
    cadence: KPI_CADENCES.includes(r.cadence as KpiCadence)
      ? (r.cadence as KpiCadence)
      : 'monthly',
    owner: typeof r.owner === 'string' ? r.owner : '',
  };
}

/**
 * Parse an untrusted serialized payload back into a KPI list. TOTAL:
 * never throws — malformed JSON or a wrong shape returns [], and
 * individually-invalid rows are dropped rather than poisoning the rest.
 */
export function parseOrgKpis(raw: unknown): OrgKpi[] {
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return [];
  }
  if (typeof data !== 'object' || data === null) return [];
  const list = (data as { kpis?: unknown }).kpis;
  if (!Array.isArray(list)) return [];
  return list.map(coerceKpi).filter((k): k is OrgKpi => k !== null);
}

/** Rows the operator actually filled in (named) — what gets persisted. */
export function filledKpis(kpis: OrgKpi[]): OrgKpi[] {
  return kpis.filter((k) => k.name.trim().length > 0);
}

/**
 * Validate the KPI grid before saving. Fully blank rows are ignored
 * (they're just spare inputs); a row with a metric/target/owner but no
 * name blocks the save. Returns a human error or null.
 */
export function validateKpis(kpis: OrgKpi[]): string | null {
  const halfFilled = kpis.some(
    (k) => !k.name.trim() && (k.metric.trim() || k.target.trim() || k.owner.trim()),
  );
  return halfFilled ? 'Each KPI needs a name — or clear the row.' : null;
}

// ---------------------------------------------------------------------------
// Governance notes (SLA + process) — TOTAL parse/serialize
// ---------------------------------------------------------------------------

export interface GovernanceNotes {
  /** Service-level commitments, e.g. "First reply < 4 business hours." */
  sla: string;
  /** How work moves, e.g. "All agent writes go through approvals." */
  process: string;
}

export const emptyGovernanceNotes: GovernanceNotes = { sla: '', process: '' };

export function serializeGovernanceNotes(notes: GovernanceNotes): string {
  return JSON.stringify(notes);
}

/** TOTAL parser — garbage in, honest empty notes out. */
export function parseGovernanceNotes(raw: unknown): GovernanceNotes {
  if (typeof raw !== 'string' || raw.trim() === '') return emptyGovernanceNotes;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return emptyGovernanceNotes;
  }
  if (typeof data !== 'object' || data === null) return emptyGovernanceNotes;
  const r = data as Record<string, unknown>;
  return {
    sla: typeof r.sla === 'string' ? r.sla : '',
    process: typeof r.process === 'string' ? r.process : '',
  };
}

// ---------------------------------------------------------------------------
// Storage identity — persisted by ./setupPersist.ts as synthetic org-scoped
// brainFacts rows (the exact mechanism brandBrain/persist.ts uses).
// ---------------------------------------------------------------------------

/** Synthetic account id — setup settings are org-wide, not per-account. */
export const SETUP_ORG_ACCOUNT_ID = '__org__';

/** Deterministic doc id for the org KPI list (one upserted row). */
export const ORG_KPIS_FACT_ID = `${SETUP_ORG_ACCOUNT_ID}:goals:orgKpis`;

/** Deterministic doc id for the governance notes (one upserted row). */
export const GOVERNANCE_NOTES_FACT_ID = `${SETUP_ORG_ACCOUNT_ID}:rules:governanceNotes`;
